import { capitalize } from './chartGeometry';

/**
 * Zodiac sign icons as vector strokes (HORO-10360).
 *
 * These used to be the Unicode characters (♈…♓). Phones swap those for colour
 * emoji whatever the font or variation selector says, so the wheel and the planet
 * cards looked different on mobile than on desktop. Drawn paths render the same
 * everywhere and take their colour from `currentColor`.
 *
 * Each glyph is drawn in a 24x24 box. Used both in HTML (a standalone <svg>) and
 * inside the chart wheel (a nested <svg> placed with x/y).
 */
const GLYPHS = {
    Aries: (
        <>
            <path d="M12 21V8.5" />
            <path d="M12 8.5C12 5 9.8 3.2 7.8 3.2 5.6 3.2 4 4.8 4 6.8 4 8.6 5.2 9.8 6.8 10" />
            <path d="M12 8.5C12 5 14.2 3.2 16.2 3.2 18.4 3.2 20 4.8 20 6.8 20 8.6 18.8 9.8 17.2 10" />
        </>
    ),
    Taurus: (
        <>
            <circle cx="12" cy="15" r="5.5" />
            <path d="M4 3.5C4 7.5 7.4 9.5 12 9.5 16.6 9.5 20 7.5 20 3.5" />
        </>
    ),
    Gemini: (
        <>
            <path d="M7.5 5.5V18.5M16.5 5.5V18.5" />
            <path d="M4 4.5C8 6.5 16 6.5 20 4.5" />
            <path d="M4 19.5C8 17.5 16 17.5 20 19.5" />
        </>
    ),
    Cancer: (
        <>
            <circle cx="8" cy="9" r="2.6" />
            <path d="M10.4 7.6C13 4.8 17.2 5 20.5 8" />
            <circle cx="16" cy="15" r="2.6" />
            <path d="M13.6 16.4C11 19.2 6.8 19 3.5 16" />
        </>
    ),
    Leo: (
        <>
            <circle cx="7.5" cy="14.5" r="3" />
            <path d="M10.5 14.5C10.5 9 10 4 14 4 17.5 4 18.5 8 17 12 16 15.2 16 19 18.4 19.8 19.8 20.2 20.8 19.4 21 18.2" />
        </>
    ),
    Virgo: (
        <>
            <path d="M3 5V17" />
            <path d="M3 8.5C3 4.5 8 4.5 8 8.5V17" />
            <path d="M8 8.5C8 4.5 13 4.5 13 8.5V16.5C13 19.5 15 20.8 17 20.2 20 19.3 20.6 14.8 17.5 14 15.8 13.6 14.2 14.4 13 16" />
        </>
    ),
    Libra: (
        <>
            <path d="M3 20.5H21" />
            <path d="M3 15.5H8.5C6.5 14 6 12 6 10.4 6 7.2 8.6 5 12 5 15.4 5 18 7.2 18 10.4 18 12 17.5 14 15.5 15.5H21" />
        </>
    ),
    Scorpio: (
        <>
            <path d="M3 5V17" />
            <path d="M3 8.5C3 4.5 8 4.5 8 8.5V17" />
            <path d="M8 8.5C8 4.5 13 4.5 13 8.5V17" />
            <path d="M13 8.5C13 4.5 18 4.5 18 8.5V17.5L21 20.5" />
            <path d="M21 16.5V20.5H17" />
        </>
    ),
    Sagittarius: (
        <>
            <path d="M4 20L20 4" />
            <path d="M12.5 4H20V11.5" />
            <path d="M6.5 11L13 17.5" />
        </>
    ),
    Capricorn: (
        <>
            <path d="M3 5L8.2 16.5V7C8.2 3.6 13 3.4 13.8 6.6L15.2 11.6C16 14.4 20.6 13.6 20.6 17 20.6 21 14.2 21 14.2 17.2" />
        </>
    ),
    Aquarius: (
        <>
            <path d="M3 9.5L6.6 6.2 10.2 9.5 13.8 6.2 17.4 9.5 21 6.2" />
            <path d="M3 17.5L6.6 14.2 10.2 17.5 13.8 14.2 17.4 17.5 21 14.2" />
        </>
    ),
    Pisces: (
        <>
            <path d="M5 3C9.2 7.2 9.2 16.8 5 21" />
            <path d="M19 3C14.8 7.2 14.8 16.8 19 21" />
            <path d="M4 12H20" />
        </>
    )
};

/** Whether we have an icon for this sign (any casing). */
export const hasZodiacGlyph = (sign) => !!GLYPHS[capitalize(String(sign || '').toLowerCase())];

/**
 * @param {string} sign       'pisces' | 'Pisces'
 * @param {number} size       pixel size, default 16
 * @param {number} [x]        position when nested inside another <svg> (the wheel)
 * @param {number} [y]
 */
const ZodiacGlyph = ({ sign, size = 16, x, y, className, strokeWidth = 1.7, ...rest }) => {
    const glyph = GLYPHS[capitalize(String(sign || '').toLowerCase())];
    if (!glyph) return null;

    return (
        <svg
            className={className}
            x={x}
            y={y}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            {...rest}
        >
            {glyph}
        </svg>
    );
};

export default ZodiacGlyph;
